import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { 
  Target, 
  Edit, 
  Save,
  Loader2, 
  CheckCircle,
  GraduationCap,
  Briefcase,
  HeartHandshake,
  Calendar,
  UserCheck,
  AlertTriangle,
  Plus,
  Hash,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Roles that can bypass employment outcome validation for retention
const RETENTION_BYPASS_ROLES = ['system_admin', 'executive', 'operations_manager'];

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const SnapEtTargetedOutcomes = ({ 
  caseId, 
  caseData, 
  onUpdate, 
  employmentOutcomeComplete = false,
  onRetentionValidationFailed,
  pendingRetentionEnable = false,
  onRetentionEnabled,
  userRole = ''
}) => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddEnrollmentDialog, setShowAddEnrollmentDialog] = useState(false);
  const [options, setOptions] = useState(null);
  const [autoOpened, setAutoOpened] = useState(false);
  const [showRetentionAlert, setShowRetentionAlert] = useState(false);
  const [editingEnrollmentId, setEditingEnrollmentId] = useState(null); // null = editing primary/legacy
  const [programEnrollments, setProgramEnrollments] = useState([]);
  const [addingEnrollment, setAddingEnrollment] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [localOutcomes, setLocalOutcomes] = useState(null);
  
  const [formData, setFormData] = useState({
    program_name: '',
    course: '',
    employment_goal: '',
    supportive_service_requests: [],
    certification_start_date: '',
    certification_end_date: '',
    abawd_status: '',
    in_retention: false,
    retention_start_date: '',
    retention_end_date: '',
    exit_outcome: '',
    exit_date: ''
  });
  const [originalExitOutcome, setOriginalExitOutcome] = useState('');

  // New enrollment form data
  const [newEnrollmentData, setNewEnrollmentData] = useState({
    program_name: '',
    course: '',
    employment_goal: '',
    supportive_service_requests: [],
    certification_start_date: '',
    certification_end_date: '',
    abawd_status: ''
  });

  // Exit Outcome options
  const EXIT_OUTCOME_OPTIONS = [
    'Currently Enrolled',
    'Successful Completion',
    'Successful Completion - Employed',
    'Successful Completion - Loss of Benefits',
    'Exited - Unsuccessful Completion',
    'Exited - Withdrawn',
    'Exited - Loss of Contact',
    'Internal Error'
  ];

  // Determine if this is a SNAP E&T case
  const isSnapEtCase = caseData?.program_name?.toLowerCase().includes('snap') && 
                       caseData?.program_name?.toLowerCase().includes('e&t');

  useEffect(() => {
    if (isSnapEtCase) {
      fetchOptions();
      loadExistingData();
      
      // Auto-open dialog if coming from enrollment creation
      if (location.state?.showTargetedOutcomes && !autoOpened && !caseData?.targeted_outcomes_data?.program_name) {
        setAutoOpened(true);
        setTimeout(() => setShowEditDialog(true), 500);
      }
    }
  }, [caseId, isSnapEtCase, location.state]);

  useEffect(() => {
    setLocalOutcomes(null);
    if (isSnapEtCase) loadExistingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData?.targeted_outcomes_data]);

  // Handle auto-enabling retention when employment outcome is completed
  useEffect(() => {
    if (pendingRetentionEnable && employmentOutcomeComplete) {
      handleAutoEnableRetention();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRetentionEnable, employmentOutcomeComplete]);

  const handleAutoEnableRetention = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 30);
      const end = endDate.toISOString().split('T')[0];
      
      const retentionData = {
        ...formData,
        in_retention: true,
        retention_start_date: today,
        retention_end_date: end
      };
      
      await axios.put(`${API}/cases/${caseId}/targeted-outcomes`, retentionData);
      toast.success('Retention has been automatically enabled');
      setFormData(retentionData);
      if (onUpdate) onUpdate();
      if (onRetentionEnabled) onRetentionEnabled();
    } catch (error) {
      console.error('Failed to auto-enable retention:', error);
      toast.error('Failed to auto-enable retention');
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await axios.get(`${API}/snap-et/options`);
      setOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch SNAP E&T options:', error);
    }
  };

  const loadExistingData = (override) => {
    const data = override || caseData?.targeted_outcomes_data;
    if (!data?.program_name && !(data?.program_enrollments?.length)) {
      setProgramEnrollments([]);
    }
    if (data) {
      
      // Check if we have the new program_enrollments array
      if (data.program_enrollments && data.program_enrollments.length > 0) {
        setProgramEnrollments(data.program_enrollments);
        // Load the first/primary enrollment into form data
        const primary = data.program_enrollments.find(e => e.is_primary) || data.program_enrollments[0];
        setFormData({
          program_name: primary.program_name || '',
          course: primary.course || '',
          employment_goal: primary.employment_goal || '',
          supportive_service_requests: primary.supportive_service_requests || [],
          certification_start_date: primary.certification_start_date || '',
          certification_end_date: primary.certification_end_date || '',
          abawd_status: primary.abawd_status || '',
          in_retention: primary.in_retention || false,
          retention_start_date: primary.retention_start_date || '',
          retention_end_date: primary.retention_end_date || '',
          exit_outcome: primary.exit_outcome || '',
          exit_date: primary.exit_date || ''
        });
      } else {
        // Legacy single enrollment format
        setFormData({
          program_name: data.program_name || '',
          course: data.course || '',
          employment_goal: data.employment_goal || '',
          supportive_service_requests: data.supportive_service_requests || [],
          certification_start_date: data.certification_start_date || '',
          certification_end_date: data.certification_end_date || '',
          abawd_status: data.abawd_status || '',
          in_retention: data.in_retention || false,
          retention_start_date: data.retention_start_date || '',
          retention_end_date: data.retention_end_date || '',
          exit_outcome: data.exit_outcome || '',
          exit_date: data.exit_date || ''
        });
        // Set as legacy enrollment
        if (data.program_name) {
          setProgramEnrollments([{
            enrollment_id: 'legacy',
            enrollment_number: 1,
            is_primary: true,
            ...data
          }]);
        }
      }
    }
  };
  
  const handleRetentionToggle = (checked) => {
    if (!checked) {
      setFormData({
        ...formData, 
        in_retention: false,
        retention_start_date: '',
        retention_end_date: ''
      });
      setShowRetentionAlert(false);
      return;
    }
    
    const canBypass = RETENTION_BYPASS_ROLES.includes(userRole);
    
    if (canBypass) {
      const today = new Date().toISOString().split('T')[0];
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 30);
      const end = endDate.toISOString().split('T')[0];
      
      setFormData({
        ...formData, 
        in_retention: true,
        retention_start_date: formData.retention_start_date || today,
        retention_end_date: formData.retention_end_date || end
      });
      setShowRetentionAlert(false);
      return;
    }
    
    if (!employmentOutcomeComplete) {
      setShowRetentionAlert(true);
      toast.warning('Employment Outcome must be completed before enabling Retention');
      if (onRetentionValidationFailed) {
        onRetentionValidationFailed();
      }
      return;
    }
    
    const today = new Date().toISOString().split('T')[0];
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 30);
    const end = endDate.toISOString().split('T')[0];
    
    setFormData({
      ...formData, 
      in_retention: true,
      retention_start_date: formData.retention_start_date || today,
      retention_end_date: formData.retention_end_date || end
    });
    setShowRetentionAlert(false);
  };
  
  const handleRetentionStartDateChange = (startDate) => {
    setFormData(prev => {
      const newData = { ...prev, retention_start_date: startDate };
      if (startDate) {
        const start = new Date(startDate);
        const end = new Date(start);
        end.setDate(end.getDate() + 30);
        newData.retention_end_date = end.toISOString().split('T')[0];
      }
      return newData;
    });
  };

  const handleOpenEdit = (enrollmentId = null) => {
    setEditingEnrollmentId(enrollmentId);
    
    if (enrollmentId && programEnrollments.length > 0) {
      // Find the specific enrollment to edit
      const enrollment = programEnrollments.find(e => e.enrollment_id === enrollmentId);
      if (enrollment) {
        setFormData({
          program_name: enrollment.program_name || '',
          course: enrollment.course || '',
          employment_goal: enrollment.employment_goal || '',
          supportive_service_requests: enrollment.supportive_service_requests || [],
          certification_start_date: enrollment.certification_start_date || '',
          certification_end_date: enrollment.certification_end_date || '',
          abawd_status: enrollment.abawd_status || '',
          in_retention: enrollment.in_retention || false,
          retention_start_date: enrollment.retention_start_date || '',
          retention_end_date: enrollment.retention_end_date || '',
          exit_outcome: enrollment.exit_outcome || '',
          exit_date: enrollment.exit_date || ''
        });
      }
    } else {
      loadExistingData();
    }
    setShowEditDialog(true);
  };

  useEffect(() => {
    if (showEditDialog) setOriginalExitOutcome(formData.exit_outcome || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditDialog]);

  const needsExitDate = (outcome) => !!outcome && outcome !== 'Currently Enrolled';

  const handleSave = async () => {
    if (needsExitDate(formData.exit_outcome) && !formData.exit_date && formData.exit_outcome !== originalExitOutcome) {
      toast.error('Please enter an Exit Date for this Exit Outcome');
      return;
    }
    if (formData.exit_date && formData.exit_date > todayLocal()) {
      toast.error('Exit Date cannot be in the future');
      return;
    }
    setSaving(true);
    try {
      if (editingEnrollmentId && editingEnrollmentId !== 'legacy' && programEnrollments.length > 1) {
        // Update specific enrollment in the array
        await axios.put(`${API}/cases/${caseId}/targeted-outcomes/enrollment/${editingEnrollmentId}`, {
          enrollment_id: editingEnrollmentId,
          ...formData
        });
        toast.success('Program enrollment updated successfully');
      } else {
        // Update legacy/primary enrollment using the original endpoint
        await axios.put(`${API}/cases/${caseId}/targeted-outcomes`, formData);
        toast.success('Targeted outcomes updated successfully');
      }
      setShowEditDialog(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save targeted outcomes');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEnrollment = async (enrollmentId) => {
    setDeletingId(enrollmentId);
    try {
      const response = await axios.delete(`${API}/cases/${caseId}/targeted-outcomes/enrollment/${enrollmentId}`);
      const updated = response.data.targeted_outcomes || {};
      setLocalOutcomes(updated);
      loadExistingData(updated);
      toast.success('Enrollment deleted');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete enrollment');
    } finally {
      setDeletingId(null);
    }
  };

  const renderDeleteButton = (enrollmentId, testId) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleDeleteEnrollment(enrollmentId)}
      disabled={deletingId === enrollmentId}
      className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
      data-testid={testId}
    >
      {deletingId === enrollmentId ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
      Delete
    </Button>
  );

  const handleOpenAddEnrollment = () => {
    const first = programEnrollments[0] || outcomesData;
    setNewEnrollmentData(prev => ({
      ...prev,
      employment_goal: first.employment_goal || '',
      abawd_status: first.abawd_status || '',
      certification_start_date: first.certification_start_date || '',
      certification_end_date: first.certification_end_date || ''
    }));
    setShowAddEnrollmentDialog(true);
  };

  const handleAddSecondEnrollment = async () => {
    if (!newEnrollmentData.program_name) {
      toast.error('Please select a program');
      return;
    }
    
    setAddingEnrollment(true);
    try {
      const response = await axios.post(`${API}/cases/${caseId}/targeted-outcomes/add-enrollment`, newEnrollmentData);
      toast.success(response.data.message || 'Second enrollment added successfully');
      setProgramEnrollments(response.data.program_enrollments || []);
      setShowAddEnrollmentDialog(false);
      setNewEnrollmentData({
        program_name: '',
        course: '',
        employment_goal: '',
        supportive_service_requests: [],
        certification_start_date: '',
        certification_end_date: '',
        abawd_status: ''
      });
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add second enrollment');
    } finally {
      setAddingEnrollment(false);
    }
  };

  const handleServiceToggle = (service, isNewEnrollment = false) => {
    if (isNewEnrollment) {
      const current = newEnrollmentData.supportive_service_requests || [];
      if (current.includes(service)) {
        setNewEnrollmentData({
          ...newEnrollmentData,
          supportive_service_requests: current.filter(s => s !== service)
        });
      } else {
        setNewEnrollmentData({
          ...newEnrollmentData,
          supportive_service_requests: [...current, service]
        });
      }
    } else {
      const current = formData.supportive_service_requests || [];
      if (current.includes(service)) {
        setFormData({
          ...formData,
          supportive_service_requests: current.filter(s => s !== service)
        });
      } else {
        setFormData({
          ...formData,
          supportive_service_requests: [...current, service]
        });
      }
    }
  };

  const showsCourse = formData.program_name === 'The HELP Institute';
  const newEnrollmentShowsCourse = newEnrollmentData.program_name === 'The HELP Institute';

  // If not SNAP E&T, show a simple text display
  if (!isSnapEtCase) {
    return (
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5" />
            Target Outcomes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {caseData?.target_outcomes ? (
            <p className="text-sm whitespace-pre-wrap">{caseData.target_outcomes}</p>
          ) : (
            <p className="text-muted-foreground text-sm">No target outcomes defined</p>
          )}
        </CardContent>
      </Card>
    );
  }

  const outcomesData = localOutcomes || caseData?.targeted_outcomes_data || {};
  const hasOutcomes = outcomesData.program_name || programEnrollments.length > 0;
  const canAddSecondEnrollment = hasOutcomes && programEnrollments.length < 2;

  // Render a single enrollment card
  const renderEnrollmentCard = (enrollment, index) => {
    const isFirst = index === 0 || enrollment.is_primary;
    const enrollmentLabel = isFirst ? 'First Enrollment' : 'Second Enrollment';
    const bgColor = isFirst ? 'bg-blue-50/50' : 'bg-green-50/50';
    const borderColor = isFirst ? 'border-blue-100' : 'border-green-100';
    const labelColor = isFirst ? 'text-blue-700 bg-blue-100' : 'text-green-700 bg-green-100';
    
    return (
      <div key={enrollment.enrollment_id || index} className={`${bgColor} rounded-lg p-4 border ${borderColor} space-y-3`}>
        <div className="flex items-center justify-between">
          <Badge variant="outline" className={labelColor}>
            <Hash className="w-3 h-3 mr-1" />
            {enrollmentLabel}
          </Badge>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleOpenEdit(enrollment.enrollment_id)}
              className="h-7 text-xs"
              data-testid={`edit-enrollment-${index}-btn`}
            >
              <Edit className="w-3 h-3 mr-1" />
              Edit
            </Button>
            {renderDeleteButton(enrollment.enrollment_id, `delete-enrollment-${index}-btn`)}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <GraduationCap className="w-4 h-4" />
              Program
            </div>
            <p className="font-medium">{enrollment.program_name}</p>
            {enrollment.course && (
              <Badge variant="secondary" className="mt-1">
                {enrollment.course}
              </Badge>
            )}
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Briefcase className="w-4 h-4" />
              Employment Goal
            </div>
            <p className="font-medium text-sm">{enrollment.employment_goal || 'Not specified'}</p>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Target className="w-4 h-4" />
              ABAWD Status
            </div>
            <Badge 
              variant={enrollment.abawd_status === 'ABAWD' ? 'destructive' : 'secondary'}
              className={enrollment.abawd_status === 'ABAWD' ? 'bg-orange-500' : ''}
            >
              {enrollment.abawd_status || 'Not set'}
            </Badge>
          </div>
        </div>
        
        {/* Certification Period */}
        <div className="pt-2 border-t border-gray-200">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <Calendar className="w-4 h-4" />
            Certification Period
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Start Date</p>
              <p className="font-medium text-sm">{enrollment.certification_start_date || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">End Date</p>
              <p className="font-medium text-sm">{enrollment.certification_end_date || 'Not set'}</p>
            </div>
          </div>
        </div>
        
        {/* Exit Outcome */}
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" /> Exit Outcome
          </p>
          {enrollment.exit_outcome ? (
            <Badge 
              variant={enrollment.exit_outcome.includes('Successful') ? 'default' : 'secondary'}
              className={enrollment.exit_outcome.includes('Successful') ? 'bg-green-600' : ''}
            >
              {enrollment.exit_outcome}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              Currently Enrolled
            </Badge>
          )}
          {needsExitDate(enrollment.exit_outcome) && enrollment.exit_date && (
            <p className="text-xs text-muted-foreground mt-1" data-testid={`enrollment-exit-date-${enrollment.enrollment_number || 1}`}>
              Exit Date: <span className="font-medium text-foreground">{enrollment.exit_date}</span>
            </p>
          )}
        </div>
        
        {/* Retention Status (each enrollment tracks retention independently) */}
        {enrollment.in_retention && (
          <div className="bg-purple-50 rounded-lg p-3 border border-purple-200 mt-2">
            <div className="flex items-center gap-2 mb-2">
              <UserCheck className="w-4 h-4 text-purple-600" />
              <span className="font-medium text-purple-800 text-sm">In Retention</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-purple-600">Start</p>
                <p className="font-medium text-purple-800 text-sm">{enrollment.retention_start_date || 'Not set'}</p>
              </div>
              <div>
                <p className="text-xs text-purple-600">End</p>
                <p className="font-medium text-purple-800 text-sm">{enrollment.retention_end_date || 'Not set'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Card className="card-shadow border-blue-200 bg-gradient-to-br from-blue-50 to-white">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
              <Target className="w-5 h-5" />
              SNAP E&T Targeted Outcomes
              {programEnrollments.length > 1 && (
                <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-700">
                  {programEnrollments.length} Enrollments
                </Badge>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {canAddSecondEnrollment && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleOpenAddEnrollment}
                  className="border-green-300 hover:bg-green-100 text-green-700"
                  data-testid="add-second-enrollment-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add 2nd Enrollment
                </Button>
              )}
              {!hasOutcomes && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleOpenEdit()}
                  className="border-blue-300 hover:bg-blue-100"
                  data-testid="edit-targeted-outcomes-btn"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Complete
                </Button>
              )}
            </div>
          </div>
          <CardDescription>Required outcomes for SNAP Employment & Training program</CardDescription>
        </CardHeader>
        <CardContent>
          {hasOutcomes ? (
            <div className="space-y-4">
              {/* Render all enrollments */}
              {programEnrollments.length > 0 ? (
                programEnrollments.map((enrollment, index) => renderEnrollmentCard(enrollment, index))
              ) : (
                // Legacy single enrollment display
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <GraduationCap className="w-4 h-4" />
                        Program
                      </div>
                      <p className="font-medium">{outcomesData.program_name}</p>
                      {outcomesData.course && (
                        <Badge variant="secondary" className="mt-1">
                          {outcomesData.course}
                        </Badge>
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Briefcase className="w-4 h-4" />
                        Employment Goal
                      </div>
                      <p className="font-medium">{outcomesData.employment_goal || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Target className="w-4 h-4" />
                        ABAWD Status
                      </div>
                      <Badge 
                        variant={outcomesData.abawd_status === 'ABAWD' ? 'destructive' : 'secondary'}
                        className={outcomesData.abawd_status === 'ABAWD' ? 'bg-orange-500' : ''}
                      >
                        {outcomesData.abawd_status || 'Not set'}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Retention Status */}
                  {outcomesData.in_retention && (
                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                      <div className="flex items-center gap-2 mb-2">
                        <UserCheck className="w-5 h-5 text-purple-600" />
                        <span className="font-medium text-purple-800">Participant is in Retention</span>
                        <Badge variant="secondary" className="bg-purple-100 text-purple-700 ml-2">
                          Retention Active
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-2 pt-2 border-t border-purple-200">
                        <div>
                          <p className="text-xs text-purple-600">Retention Start</p>
                          <p className="font-medium text-purple-800">
                            {outcomesData.retention_start_date || 'Not set'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-purple-600">Retention End</p>
                          <p className="font-medium text-purple-800">
                            {outcomesData.retention_end_date || 'Not set'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Certification Period */}
                  <div className="bg-blue-50/50 rounded-lg p-3 border border-blue-100">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Calendar className="w-4 h-4" />
                      Certification Period
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Start Date</p>
                        <p className="font-medium">{outcomesData.certification_start_date || 'Not set'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">End Date</p>
                        <p className="font-medium">{outcomesData.certification_end_date || 'Not set'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Exit Outcome */}
                  <div className="bg-gray-50/50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <CheckCircle className="w-4 h-4" />
                      Exit Outcome
                    </div>
                    <div>
                      {outcomesData.exit_outcome ? (
                        <Badge 
                          variant={outcomesData.exit_outcome.includes('Successful') ? 'default' : 'secondary'}
                          className={outcomesData.exit_outcome.includes('Successful') ? 'bg-green-600' : ''}
                          data-testid="exit-outcome-badge"
                        >
                          {outcomesData.exit_outcome}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          Currently Enrolled
                        </Badge>
                      )}
                      {needsExitDate(outcomesData.exit_outcome) && outcomesData.exit_date && (
                        <p className="text-xs text-muted-foreground mt-1" data-testid="exit-date-display">
                          Exit Date: <span className="font-medium text-foreground">{outcomesData.exit_date}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {outcomesData.supportive_service_requests?.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <HeartHandshake className="w-4 h-4" />
                        Supportive Services Requested
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {outcomesData.supportive_service_requests.map((service) => (
                          <Badge key={service} variant="outline" className="bg-white">
                            <CheckCircle className="w-3 h-3 mr-1 text-green-600" />
                            {service}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-2">
                    {renderDeleteButton('legacy', 'delete-targeted-outcomes-btn')}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleOpenEdit()}
                      className="border-blue-300 hover:bg-blue-100"
                      data-testid="edit-targeted-outcomes-btn"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 bg-yellow-50 rounded-lg border border-yellow-200">
              <Target className="w-10 h-10 mx-auto mb-2 text-yellow-600" />
              <p className="text-yellow-800 font-medium">Targeted Outcomes Required</p>
              <p className="text-sm text-yellow-700 mb-3">
                Please complete the targeted outcomes for this SNAP E&T enrollment.
              </p>
              <Button 
                onClick={() => handleOpenEdit()} 
                className="bg-yellow-600 hover:bg-yellow-700"
                data-testid="complete-targeted-outcomes-btn"
              >
                Complete Now
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEnrollmentId && programEnrollments.length > 1 
                ? `Edit ${programEnrollments.find(e => e.enrollment_id === editingEnrollmentId)?.is_primary ? 'First' : 'Second'} Enrollment`
                : 'SNAP E&T Targeted Outcomes'
              }
            </DialogTitle>
            <DialogDescription>
              Configure the targeted outcomes for this SNAP Employment & Training case
            </DialogDescription>
          </DialogHeader>
          
          {options ? (
            <div className="space-y-4 py-4">
              {/* Program Name */}
              <div className="space-y-2">
                <Label>Program Name *</Label>
                <Select
                  value={formData.program_name || "none"}
                  onValueChange={(value) => setFormData({
                    ...formData, 
                    program_name: value === "none" ? "" : value,
                    course: value !== 'The HELP Institute' ? '' : formData.course
                  })}
                >
                  <SelectTrigger data-testid="snap-et-program-select">
                    <SelectValue placeholder="Select program" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a program</SelectItem>
                    {options.program_names.map((name) => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Course (only for The HELP Institute) */}
              {showsCourse && (
                <div className="space-y-2">
                  <Label>Course *</Label>
                  <Select
                    value={formData.course || "none"}
                    onValueChange={(value) => setFormData({...formData, course: value === "none" ? "" : value})}
                  >
                    <SelectTrigger data-testid="snap-et-course-select">
                      <SelectValue placeholder="Select course" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select a course</SelectItem>
                      {options.help_institute_courses.map((course) => (
                        <SelectItem key={course} value={course}>{course}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Employment Goal */}
              <div className="space-y-2">
                <Label>Employment Goal</Label>
                <Input
                  value={formData.employment_goal}
                  onChange={(e) => setFormData({...formData, employment_goal: e.target.value})}
                  placeholder="e.g., Obtain full-time employment in retail"
                  data-testid="snap-et-employment-goal"
                />
              </div>

              {/* ABAWD Status */}
              <div className="space-y-2">
                <Label>ABAWD Status *</Label>
                <Select
                  value={formData.abawd_status || "none"}
                  onValueChange={(value) => setFormData({...formData, abawd_status: value === "none" ? "" : value})}
                >
                  <SelectTrigger data-testid="snap-et-abawd-status">
                    <SelectValue placeholder="Select ABAWD status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select ABAWD status</SelectItem>
                    {(options.abawd_status_options || ['ABAWD', 'Non-ABAWD']).map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  ABAWD = Able-Bodied Adults Without Dependents
                </p>
              </div>
              
              {/* Retention Toggle - available for all enrollments independently */}
              <div className="space-y-2 bg-purple-50 rounded-lg p-3 border border-purple-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-purple-600" />
                    <Label htmlFor="retention-toggle" className="font-medium text-purple-800">
                      Retention
                    </Label>
                  </div>
                  <Switch
                    id="retention-toggle"
                    checked={formData.in_retention}
                    onCheckedChange={(checked) => handleRetentionToggle(checked)}
                    data-testid="snap-et-retention-toggle"
                  />
                </div>
                <p className="text-xs text-purple-700">
                  Enable this toggle when the participant is in retention phase for this enrollment
                </p>
                
                {showRetentionAlert && (
                  <Alert variant="destructive" className="mt-2 bg-orange-50 border-orange-300">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <AlertTitle className="text-orange-800">Employment Outcome Required</AlertTitle>
                    <AlertDescription className="text-orange-700">
                      The Participant Employment Outcome card must be completed before Retention can be enabled. 
                      The Employment Outcome card has been opened for you to complete.
                    </AlertDescription>
                  </Alert>
                )}
                
                {formData.in_retention && (
                  <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-purple-200">
                    <div className="space-y-1">
                      <Label className="text-xs text-purple-700">Retention Start Date</Label>
                      <Input
                        type="date"
                        value={formData.retention_start_date}
                        onChange={(e) => handleRetentionStartDateChange(e.target.value)}
                        className="bg-white border-purple-300 focus:border-purple-500"
                        data-testid="snap-et-retention-start-date"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-purple-700">Retention End Date</Label>
                      <Input
                        type="date"
                        value={formData.retention_end_date}
                        onChange={(e) => setFormData({...formData, retention_end_date: e.target.value})}
                        className="bg-white border-purple-300 focus:border-purple-500"
                        data-testid="snap-et-retention-end-date"
                      />
                      <p className="text-xs text-purple-600">
                        Auto-calculates to 30 days after start, but can be adjusted
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Certification Period */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Certification Period
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Start Date</Label>
                    <Input
                      type="date"
                      value={formData.certification_start_date}
                      onChange={(e) => setFormData({...formData, certification_start_date: e.target.value})}
                      data-testid="snap-et-cert-start-date"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">End Date</Label>
                    <Input
                      type="date"
                      value={formData.certification_end_date}
                      onChange={(e) => setFormData({...formData, certification_end_date: e.target.value})}
                      data-testid="snap-et-cert-end-date"
                    />
                  </div>
                </div>
              </div>

              {/* Exit Outcome */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Exit Outcome
                </Label>
                <Select 
                  value={formData.exit_outcome} 
                  onValueChange={(v) => {
                    const outcome = v === '_none' ? '' : v;
                    setFormData({...formData, exit_outcome: outcome, exit_date: outcome === formData.exit_outcome ? formData.exit_date : ''});
                  }}
                >
                  <SelectTrigger data-testid="snap-et-exit-outcome-select">
                    <SelectValue placeholder="Select exit outcome" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-- Select Exit Outcome --</SelectItem>
                    {EXIT_OUTCOME_OPTIONS.map((outcome) => (
                      <SelectItem key={outcome} value={outcome}>{outcome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Exit Outcome will sync with Caseload Review automatically
                </p>
                {needsExitDate(formData.exit_outcome) && (
                  <div className="space-y-1 pt-1">
                    <Label htmlFor="snap-et-exit-date">
                      Exit Date {formData.exit_outcome !== originalExitOutcome || formData.exit_date ? '*' : ''}
                    </Label>
                    <Input
                      id="snap-et-exit-date"
                      type="date"
                      max={todayLocal()}
                      value={formData.exit_date || ''}
                      onChange={(e) => setFormData({...formData, exit_date: e.target.value})}
                      data-testid="snap-et-exit-date-input"
                    />
                  </div>
                )}
              </div>

              {/* Supportive Service Requests */}
              <div className="space-y-2">
                <Label>Supportive Service Requests</Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-3 border rounded-md bg-gray-50">
                  {options.supportive_service_requests.map((service) => (
                    <label 
                      key={service} 
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white p-1 rounded"
                    >
                      <Checkbox
                        checked={(formData.supportive_service_requests || []).includes(service)}
                        onCheckedChange={() => handleServiceToggle(service)}
                      />
                      {service}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">Loading options...</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !formData.program_name}
              data-testid="save-targeted-outcomes-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Outcomes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Second Enrollment Dialog */}
      <Dialog open={showAddEnrollmentDialog} onOpenChange={setShowAddEnrollmentDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-600" />
              Add Second Program Enrollment
            </DialogTitle>
            <DialogDescription>
              Add a second program enrollment without affecting the first enrollment's data.
            </DialogDescription>
          </DialogHeader>
          
          {options ? (
            <div className="space-y-4 py-4">
              {/* Program Name */}
              <div className="space-y-2">
                <Label>Program Name *</Label>
                <Select
                  value={newEnrollmentData.program_name || "none"}
                  onValueChange={(value) => setNewEnrollmentData({
                    ...newEnrollmentData, 
                    program_name: value === "none" ? "" : value,
                    course: value !== 'The HELP Institute' ? '' : newEnrollmentData.course
                  })}
                >
                  <SelectTrigger data-testid="new-enrollment-program-select">
                    <SelectValue placeholder="Select program" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a program</SelectItem>
                    {[...options.program_names, ...(options.program_names.includes('Supervised Job Search') ? [] : ['Supervised Job Search'])].map((name) => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Course (only for The HELP Institute) */}
              {newEnrollmentShowsCourse && (
                <div className="space-y-2">
                  <Label>Course</Label>
                  <Select
                    value={newEnrollmentData.course || "none"}
                    onValueChange={(value) => setNewEnrollmentData({...newEnrollmentData, course: value === "none" ? "" : value})}
                  >
                    <SelectTrigger data-testid="new-enrollment-course-select">
                      <SelectValue placeholder="Select course" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select a course</SelectItem>
                      {options.help_institute_courses.map((course) => (
                        <SelectItem key={course} value={course}>{course}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Employment Goal */}
              <div className="space-y-2">
                <Label>Employment Goal</Label>
                <Input
                  value={newEnrollmentData.employment_goal}
                  onChange={(e) => setNewEnrollmentData({...newEnrollmentData, employment_goal: e.target.value})}
                  placeholder="e.g., Obtain full-time employment in retail"
                  data-testid="new-enrollment-employment-goal"
                />
              </div>

              {/* ABAWD Status */}
              <div className="space-y-2">
                <Label>ABAWD Status</Label>
                <Select
                  value={newEnrollmentData.abawd_status || "none"}
                  onValueChange={(value) => setNewEnrollmentData({...newEnrollmentData, abawd_status: value === "none" ? "" : value})}
                >
                  <SelectTrigger data-testid="new-enrollment-abawd-status">
                    <SelectValue placeholder="Select ABAWD status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select ABAWD status</SelectItem>
                    {(options.abawd_status_options || ['ABAWD', 'Non-ABAWD']).map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Certification Period */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Certification Period
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Start Date</Label>
                    <Input
                      type="date"
                      value={newEnrollmentData.certification_start_date}
                      onChange={(e) => setNewEnrollmentData({...newEnrollmentData, certification_start_date: e.target.value})}
                      data-testid="new-enrollment-cert-start-date"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">End Date</Label>
                    <Input
                      type="date"
                      value={newEnrollmentData.certification_end_date}
                      onChange={(e) => setNewEnrollmentData({...newEnrollmentData, certification_end_date: e.target.value})}
                      data-testid="new-enrollment-cert-end-date"
                    />
                  </div>
                </div>
              </div>

              {/* Supportive Service Requests */}
              <div className="space-y-2">
                <Label>Supportive Service Requests</Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-3 border rounded-md bg-gray-50">
                  {options.supportive_service_requests.map((service) => (
                    <label 
                      key={service} 
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white p-1 rounded"
                    >
                      <Checkbox
                        checked={(newEnrollmentData.supportive_service_requests || []).includes(service)}
                        onCheckedChange={() => handleServiceToggle(service, true)}
                      />
                      {service}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">Loading options...</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEnrollmentDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddSecondEnrollment} 
              disabled={addingEnrollment || !newEnrollmentData.program_name}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-second-enrollment-btn"
            >
              {addingEnrollment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Enrollment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SnapEtTargetedOutcomes;
